import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, Filter, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface HomeSearchFiltersProps {
  onSearchChange: (term: string) => void;
  onSportChange: (sport: string | null) => void;
  onAgeGroupChange: (ageGroup: string | null) => void;
  className?: string;
}

const AGE_GROUPS = [
  { value: 'youth', label: 'Youth (Under 13)' },
  { value: 'teen', label: 'Teen (13-17)' },
  { value: 'adult', label: 'Adult (18+)' },
  { value: 'senior', label: 'Senior (35+)' },
];

const HomeSearchFilters: React.FC<HomeSearchFiltersProps> = ({
  onSearchChange,
  onSportChange,
  onAgeGroupChange,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('');
  const [sports, setSports] = useState<{ id: number; sport_name: string }[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch available sports
  useEffect(() => {
    const fetchSports = async () => {
      const { data } = await supabase
        .from('available_sports')
        .select('id, sport_name')
        .order('sport_name');
      if (data) setSports(data);
    };
    fetchSports();
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleSportChange = (value: string) => {
    setSelectedSport(value);
    onSportChange(value === 'all' ? null : value);
  };

  const handleAgeGroupChange = (value: string) => {
    setSelectedAgeGroup(value);
    onAgeGroupChange(value === 'all' ? null : value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSport('');
    setSelectedAgeGroup('');
    onSearchChange('');
    onSportChange(null);
    onAgeGroupChange(null);
  };

  const hasActiveFilters = searchTerm || selectedSport || selectedAgeGroup;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Main Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search players, teams, cities..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="icon"
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'bg-primary' : ''}
        >
          <Filter className="h-4 w-4" />
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={clearFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Filter Row */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 p-4 bg-muted/30 rounded-lg animate-in slide-in-from-top-2">
          <Select value={selectedSport} onValueChange={handleSportChange}>
            <SelectTrigger className="w-[160px] bg-background">
              <SelectValue placeholder="All Sports" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport.id} value={sport.sport_name}>
                  {sport.sport_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedAgeGroup} onValueChange={handleAgeGroupChange}>
            <SelectTrigger className="w-[160px] bg-background">
              <SelectValue placeholder="All Ages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {AGE_GROUPS.map(age => (
                <SelectItem key={age.value} value={age.value}>
                  {age.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              Clear all filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default HomeSearchFilters;
